import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Phone, FileText, Shield, Star, MapPin, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

const HeroSection = ({ logoUrl }) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <motion.section
      ref={ref}
      style={isMobile ? {} : { opacity }}
      className="relative min-h-screen flex items-center overflow-hidden noise-overlay"
    >
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #1e3a8a 0%, #2563eb 25%, #3b82f6 50%, #2563eb 75%, #1e40af 100%)',
        }}
      />

      {/* Background shapes - hidden on mobile for perf */}
      {!isMobile && (
        <>
          <div className="absolute top-0 left-0 w-[700px] h-[700px] opacity-[0.08]">
            <div className="w-full h-full blob-shape" style={{ background: 'radial-gradient(circle, #60a5fa, transparent)', filter: 'blur(100px)' }} />
          </div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] opacity-[0.06]">
            <div className="w-full h-full blob-shape-2" style={{ background: 'radial-gradient(circle, #93c5fd, transparent)', filter: 'blur(80px)' }} />
          </div>
        </>
      )}

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 w-full pt-24 sm:pt-28 pb-16 sm:pb-20 relative z-10">
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Logo - on top for mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.8, type: 'spring', stiffness: 80 }}
            className="order-1 lg:order-2 flex justify-center"
          >
            <div className="relative">
              {/* Rings - only on desktop */}
              {!isMobile && (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 m-auto w-[115%] h-[115%] rounded-full"
                    style={{ border: '1px solid rgba(96, 165, 250, 0.06)', borderTop: '1px solid rgba(96, 165, 250, 0.25)' }}
                  />
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 m-auto w-[130%] h-[130%] rounded-full"
                    style={{ border: '1px dashed rgba(96, 165, 250, 0.04)' }}
                  />
                </>
              )}

              {/* Glow */}
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(96, 165, 250, 0.15) 0%, transparent 70%)',
                  transform: 'scale(1.3)',
                  filter: 'blur(25px)',
                }}
              />

              {/* Logo image - smaller on mobile, no float animation on mobile */}
              <motion.div
                animate={isMobile ? {} : { y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <img
                  src={logoUrl}
                  alt="ג'יני מובייל"
                  className="w-44 h-44 sm:w-64 sm:h-64 lg:w-[340px] lg:h-[340px] rounded-full object-cover"
                  style={{
                    boxShadow: '0 0 40px rgba(96, 165, 250, 0.12), 0 0 80px rgba(96, 165, 250, 0.05)',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                  }}
                />
              </motion.div>

              {/* Floating badge - hidden on very small screens */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 1.2, duration: 0.5, type: 'spring' }}
                className="hidden sm:flex absolute -bottom-2 right-2 items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-medium"
                style={{
                  background: 'rgba(37, 99, 235, 0.5)',
                  border: '1px solid rgba(61, 168, 158, 0.3)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Shield size={14} style={{ color: '#4ade80' }} />
                <span className="text-blue-50 font-semibold">אחריות מלאה</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Text Content */}
          <div className="order-2 lg:order-1 text-center lg:text-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 sm:px-5 sm:py-2 rounded-full mb-6 sm:mb-8 text-xs sm:text-sm font-bold"
              style={{
                background: 'linear-gradient(135deg, #c9a84c 0%, #e8d48b 50%, #c9a84c 100%)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: '#1e3a8a',
                boxShadow: '0 4px 15px rgba(201, 168, 76, 0.4)',
              }}
            >
              <MapPin size={16} style={{ color: '#c9a84c', flexShrink: 0 }} />
              {t('hero.badge')}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.7 }}
            >
              <h1 className="text-3xl sm:text-5xl lg:text-[3.5rem] font-black leading-tight mb-1 sm:mb-2 text-white" style={{ fontFamily: 'Heebo, sans-serif' }}>
                {t('hero.title')}
              </h1>
              <h1 className="text-3xl sm:text-5xl lg:text-[3.5rem] font-black leading-tight mb-4 sm:mb-6 gold-text-shimmer" style={{ fontFamily: 'Heebo, sans-serif' }}>
                {t('hero.subtitle')}
              </h1>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="text-sm sm:text-lg text-blue-100 mb-8 sm:mb-10 leading-relaxed max-w-lg mx-auto lg:ms-0"
            >
              {t('hero.description')}
              {' '}<span style={{ color: '#6ee7b7' }} className="font-bold">{t('hero.highlight')}</span>
              {' '}<span className="font-bold text-white">{t('hero.warranty')}</span>
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12 items-center lg:items-start justify-center lg:justify-start"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-bold text-black magnetic-btn overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #c9a84c 0%, #e8d48b 50%, #c9a84c 100%)',
                  boxShadow: '0 6px 25px rgba(201, 168, 76, 0.35)',
                }}
                onClick={() => window.open('tel:0532222342')}
              >
                <div
                  className="absolute inset-0 opacity-30"
                  style={{
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s ease-in-out infinite',
                  }}
                />
                <Phone size={18} className="relative z-10" />
                <span className="relative z-10">{t('hero.callButton')}</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold text-white"
                style={{
                  background: 'linear-gradient(135deg, #60a5fa 0%, #93c5fd 50%, #7dd3fc 100%)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 4px 15px rgba(96, 165, 250, 0.4)',
                  color: '#ffffff',
                }}
                onClick={() => {
                  const el = document.querySelector('#contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <FileText size={18} />
                {t('hero.quoteButton')}
              </motion.button>
            </motion.div>

            {/* Bottom badges - 2x2 grid on mobile */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1, duration: 0.6 }}
              className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2 sm:gap-3 lg:gap-6 justify-center lg:justify-start px-2"
            >
              {[
                { icon: Shield, text: t('hero.badges.warranty') },
                { icon: Star, text: t('hero.badges.professional') },
                { icon: MapPin, text: t('hero.badges.petahTikva') },
                { icon: MapPin, text: t('hero.badges.sharon') },
              ].map((badge, i) => (
                <div 
                  key={i} 
                  className="flex items-center justify-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1.5 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.25), rgba(147, 197, 253, 0.35))',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 2px 10px rgba(96, 165, 250, 0.3)',
                    backdropFilter: 'blur(10px)',
                    color: '#ffffff',
                  }}
                >
                  <badge.icon size={12} style={{ color: '#c9a84c', flexShrink: 0 }} />
                  <span className="whitespace-nowrap text-[9px] sm:text-xs leading-tight">{badge.text}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll indicator - hidden on mobile */}
      <motion.div
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        className="hidden sm:block absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer z-10"
        onClick={() => {
          const el = document.querySelector('#why-us');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }}
      >
        <div
          className="w-8 h-12 rounded-full flex items-end justify-center pb-2"
          style={{ border: '2px solid rgba(147, 197, 253, 0.4)' }}
        >
          <motion.div
            animate={{ y: [0, 6, 0], opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1 h-2 rounded-full"
            style={{ background: '#60a5fa' }}
          />
        </div>
      </motion.div>

      <div
        className="absolute bottom-0 left-0 right-0 h-20 sm:h-32"
        style={{ background: 'linear-gradient(to top, #1e3a8a, transparent)' }}
      />
    </motion.section>
  );
};

export default HeroSection;
